"use client";

import {
  AUTODIALER_BRIDGE_HANDSHAKE_TIMEOUT_MS,
  AUTODIALER_BRIDGE_KEEPALIVE_PING_MS,
  AUTODIALER_BRIDGE_REPAIR_MAX_ATTEMPTS,
  AUTODIALER_BRIDGE_WATCHDOG_INTERVAL_MS,
  AUTODIALER_STATE_HEARTBEAT_MS,
  autodialerBridgeRepairBackoffMs,
  autodialerCallChannelName,
  autodialerCallWindowName,
  autodialerCallWindowPath,
  clearAutodialerCallWindowId,
  decideAutodialerHandshakeTimeout,
  didConfirmAutodialerDialCancel,
  getOrCreateAutodialerCallWindowId,
  getStoredAutodialerCallWindowId,
  isBlankAutodialerCallWindow,
  shouldKeepAutodialerBridgeAfterPopupHandleClosed,
  shouldRecreateAutodialerChannel,
  shouldCheckAutodialerBridgeHealth,
  shouldDeferAutodialerHandshakeAfterBackground,
  shouldSpawnAutodialerCallWindowAfterBlankProbe,
  type AutodialerCallStatePayload,
  type AutodialerCallWindowAttachStatus,
  type AutodialerDialPayload,
  type AutodialerMainToPopupMessage,
  type AutodialerPopupToMainMessage,
} from "@/app/_components/adminWorkflows/autodialerCallChannel";
import getLogger from "@/lib/logger";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = getLogger("app:adminWorkflows:autodialerCallWindow");

/** How often the main window polls `.closed` — the only reliable way to
 * detect an admin closing the popup (a closed context can't broadcast). */
const CLOSED_POLL_INTERVAL_MS = 1000;
/** How long to wait for a HANG_UP_RESULT before giving up and proceeding. */
const HANG_UP_RPC_TIMEOUT_MS = 8000;
/** How long to wait for a CANCEL_DIAL_RESULT before giving up. A timeout is
 * not a successful cancel — the popup may already be connecting. */
const CANCEL_DIAL_RPC_TIMEOUT_MS = 2000;

const STALE_ERROR_MESSAGE =
  "The call window isn't responding. Reopen it to keep dialing.";
const CLOSED_ERROR_MESSAGE =
  "The call window was closed. Select Start call to reopen it.";

export type { AutodialerCallWindowAttachStatus };

export type AutodialerCallWindowMessageHandler = (
  message: AutodialerPopupToMainMessage,
) => void;

export interface AutodialerCallWindowBridge {
  attachStatus: AutodialerCallWindowAttachStatus;
  /** True only while this tab drives the popup — keepalive pings, closed-window
   * polling and channel repair all running. A listen-only bind reports
   * `attachStatus: "attached"` as soon as it overhears the popup, so that
   * status alone cannot tell you whether anyone is keeping the popup alive. */
  isDrivingCallWindow: boolean;
  /** Human-readable reason for a "blocked" / "stale" status, for the connection-error banner. */
  errorMessage: string | null;
  /** Open (or reattach to) the popup without raising it. Must be called from
   * inside a real user gesture (e.g. the "Start Workflow" click) the first
   * time a popup id is created — reattaching to an already-open window does
   * not need a gesture. Start call uses this so dialing does not steal
   * focus from prep. */
  open: () => void;
  /** Bring the existing popup to the front without navigating it (that
   * would remount the Device). Must be called from a user gesture. Do not
   * call this from Start call. */
  focus: () => void;
  /** Silently reattach to a still-running popup on mount, if one exists. No-op otherwise.
   * Pass `{ probeNamedWindow: false }` from a secondary tab so a failed named
   * lookup cannot open a blank window or clear the shared popup id. */
  reattachIfPresent: (options?: { probeNamedWindow?: boolean }) => void;
  /** Tear down the popup at the end of a workflow session. */
  close: () => void;
  /** Send the next call's dial params to the popup. */
  dial: (payload: AutodialerDialPayload) => void;
  /** Drop a DIAL that has not started connecting yet. Resolves true only if
   * the popup confirmed the cancel. A timeout resolves false. */
  cancelDial: (taskId: number) => Promise<boolean>;
  /** Ask the popup to hang up the live call; resolves once it confirms (or after a timeout). */
  hangUp: (taskId: number, optionId?: string) => Promise<void>;
  /** Toggle mute on the popup's live call. */
  toggleMute: () => void;
  /** Subscribe to every message from the popup. Returns an unsubscribe function. */
  onMessage: (handler: AutodialerCallWindowMessageHandler) => () => void;
}

/**
 * Owns the autodialer's popup call window: opening/reattaching it, polling
 * for the admin closing it, and the `BroadcastChannel` used to drive the call
 * from the main window. If that channel goes silent while the popup is still
 * open, it is recreated in place (no new popup, no Twilio hang-up).
 * Instantiate once per `CallWorkflowProvider` — not per task — so the same
 * popup persists across every call in the queue.
 */
export function useAutodialerCallWindow(
  userId: number | null,
): AutodialerCallWindowBridge {
  const [attachStatus, setAttachStatus] =
    useState<AutodialerCallWindowAttachStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isDrivingCallWindow, setIsDrivingCallWindow] = useState(false);

  const popupIdRef = useRef<string | null>(null);
  const windowRef = useRef<Window | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const closedPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const keepalivePingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const silenceWatchdogRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const handshakeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const repairBackoffRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readyRef = useRef(false);
  const tearingDownRef = useRef(false);
  const lastInboundAtRef = useRef<number | null>(null);
  // Only ever stamped by a real message from the popup, so a background tick
  // cannot disguise a popup that was X-closed. Drives the close detection in
  // the handle-closed poll below; the watchdog reads the liveness clock.
  const lastBridgeActivityAtRef = useRef<number | null>(null);
  const lastBackgroundAtRef = useRef<number | null>(null);
  const lastRecreateAtRef = useRef(0);
  const repairAttemptCountRef = useRef(0);
  const repairInFlightRef = useRef(false);
  const hadPriorInboundRef = useRef(false);
  const listenersRef = useRef<Set<AutodialerCallWindowMessageHandler>>(
    new Set(),
  );
  const pendingHangUpsRef = useRef<Map<string, () => void>>(new Map());
  const pendingCancelDialsRef = useRef<
    Map<string, (cancelled: boolean) => void>
  >(new Map());
  const confirmHandleClosedTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);

  const clearHandshakeTimeout = useCallback(() => {
    if (handshakeTimeoutRef.current) {
      clearTimeout(handshakeTimeoutRef.current);
      handshakeTimeoutRef.current = null;
    }
  }, []);

  const clearRepairBackoff = useCallback(() => {
    if (repairBackoffRef.current) {
      clearTimeout(repairBackoffRef.current);
      repairBackoffRef.current = null;
    }
  }, []);

  const clearClosedPoll = useCallback(() => {
    if (closedPollRef.current) {
      clearInterval(closedPollRef.current);
      closedPollRef.current = null;
    }
  }, []);

  const clearConfirmHandleClosedTimeout = useCallback(() => {
    if (confirmHandleClosedTimeoutRef.current) {
      clearTimeout(confirmHandleClosedTimeoutRef.current);
      confirmHandleClosedTimeoutRef.current = null;
    }
  }, []);

  const clearKeepalivePing = useCallback(() => {
    if (keepalivePingRef.current) {
      clearInterval(keepalivePingRef.current);
      keepalivePingRef.current = null;
    }
  }, []);

  const clearSilenceWatchdog = useCallback(() => {
    if (silenceWatchdogRef.current) {
      clearInterval(silenceWatchdogRef.current);
      silenceWatchdogRef.current = null;
    }
  }, []);

  const teardownChannel = useCallback(() => {
    channelRef.current?.close();
    channelRef.current = null;
  }, []);

  const postToPopup = useCallback((message: AutodialerMainToPopupMessage) => {
    channelRef.current?.postMessage(message);
  }, []);

  const markStale = useCallback(() => {
    repairInFlightRef.current = false;
    setAttachStatus("stale");
    setErrorMessage(STALE_ERROR_MESSAGE);
  }, []);

  const canCheckBridgeHealth = useCallback(() => {
    let isPopupHidden = false;
    try {
      isPopupHidden = windowRef.current?.document.visibilityState === "hidden";
    } catch {
      // An inaccessible popup still needs the normal failure detection.
    }
    const canCheck = shouldCheckAutodialerBridgeHealth({
      isMainHidden: document.visibilityState === "hidden",
      isPopupHidden,
    });
    if (!canCheck) {
      lastBackgroundAtRef.current = Date.now();
    }
    return canCheck;
  }, []);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        lastBackgroundAtRef.current = Date.now();
        lastBridgeActivityAtRef.current = Date.now();
        if (channelRef.current && !tearingDownRef.current) {
          postToPopup({ type: "PING" });
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [postToPopup]);

  const bindChannelRef = useRef<(popupId: string) => void>(() => {});
  const startRepairRef = useRef<() => void>(() => {});

  const handleChannelMessage = useCallback(
    (message: AutodialerPopupToMainMessage) => {
      if (message.type === "CLOSING") {
        tearingDownRef.current = true;
        clearHandshakeTimeout();
        clearRepairBackoff();
        clearKeepalivePing();
        clearSilenceWatchdog();
        setIsDrivingCallWindow(false);
        setAttachStatus("closed_by_admin");
      } else {
        lastInboundAtRef.current = Date.now();
        lastBridgeActivityAtRef.current = lastInboundAtRef.current;
        hadPriorInboundRef.current = true;
        readyRef.current = true;
        repairInFlightRef.current = false;
        repairAttemptCountRef.current = 0;
        clearHandshakeTimeout();
        clearRepairBackoff();
        setAttachStatus("attached");
        setErrorMessage(null);

        if (message.type === "HANG_UP_RESULT") {
          const resolve = pendingHangUpsRef.current.get(message.requestId);
          if (resolve) {
            pendingHangUpsRef.current.delete(message.requestId);
            resolve();
          }
        } else if (message.type === "CANCEL_DIAL_RESULT") {
          const resolve = pendingCancelDialsRef.current.get(message.requestId);
          if (resolve) {
            pendingCancelDialsRef.current.delete(message.requestId);
            resolve(message.cancelled);
          }
        }
      }
      listenersRef.current.forEach((listener) => listener(message));
    },
    [
      clearHandshakeTimeout,
      clearKeepalivePing,
      clearRepairBackoff,
      clearSilenceWatchdog,
    ],
  );

  const bindChannel = useCallback(
    (popupId: string, options?: { listenOnly?: boolean }) => {
      teardownChannel();
      const channel = new BroadcastChannel(autodialerCallChannelName(popupId));
      channel.onmessage = (event: MessageEvent<AutodialerPopupToMainMessage>) =>
        handleChannelMessage(event.data);
      channel.onmessageerror = () => {
        logger.warn(
          { popupId },
          "Autodialer call channel failed to deserialize a message",
        );
        if (!options?.listenOnly) {
          startRepairRef.current();
        }
      };
      channelRef.current = channel;
      lastRecreateAtRef.current = Date.now();
      readyRef.current = false;

      if (options?.listenOnly) {
        setIsDrivingCallWindow(false);
        return;
      }

      clearHandshakeTimeout();
      const checkHandshake = () => {
        if (
          !canCheckBridgeHealth() ||
          shouldDeferAutodialerHandshakeAfterBackground({
            lastBackgroundAtMs: lastBackgroundAtRef.current,
            nowMs: Date.now(),
          })
        ) {
          handshakeTimeoutRef.current = setTimeout(
            checkHandshake,
            AUTODIALER_BRIDGE_HANDSHAKE_TIMEOUT_MS,
          );
          postToPopup({ type: "PING" });
          return;
        }
        const decision = decideAutodialerHandshakeTimeout({
          receivedReady: readyRef.current,
          hadPriorInbound: hadPriorInboundRef.current,
          repairAttemptCount: repairAttemptCountRef.current,
        });
        if (decision === "retry") {
          const delay = autodialerBridgeRepairBackoffMs(
            repairAttemptCountRef.current,
          );
          logger.warn(
            { popupId, delay, attempt: repairAttemptCountRef.current },
            "Autodialer call window handshake timed out; retrying channel",
          );
          repairBackoffRef.current = setTimeout(() => {
            repairBackoffRef.current = null;
            repairInFlightRef.current = false;
            startRepairRef.current();
          }, delay);
        } else if (decision === "give_up") {
          logger.warn(
            { popupId },
            "Autodialer call window did not answer handshake in time",
          );
          markStale();
        }
      };
      handshakeTimeoutRef.current = setTimeout(
        checkHandshake,
        AUTODIALER_BRIDGE_HANDSHAKE_TIMEOUT_MS,
      );

      postToPopup({ type: "PING" });
    },
    [
      clearHandshakeTimeout,
      canCheckBridgeHealth,
      handleChannelMessage,
      markStale,
      postToPopup,
      teardownChannel,
    ],
  );

  const startRepair = useCallback(() => {
    const popupId = popupIdRef.current;
    if (
      !popupId ||
      !canCheckBridgeHealth() ||
      tearingDownRef.current ||
      windowRef.current?.closed ||
      repairInFlightRef.current
    ) {
      return;
    }
    if (
      repairAttemptCountRef.current >= AUTODIALER_BRIDGE_REPAIR_MAX_ATTEMPTS
    ) {
      markStale();
      return;
    }
    repairAttemptCountRef.current += 1;
    repairInFlightRef.current = true;
    logger.warn(
      { popupId, attempt: repairAttemptCountRef.current },
      "Recreating autodialer call channel",
    );
    bindChannelRef.current(popupId);
  }, [canCheckBridgeHealth, markStale]);

  useEffect(() => {
    bindChannelRef.current = bindChannel;
    startRepairRef.current = startRepair;
  });

  const attachToWindow = useCallback(
    (popupId: string, win: Window) => {
      tearingDownRef.current = false;
      windowRef.current = win;
      popupIdRef.current = popupId;
      readyRef.current = false;
      lastInboundAtRef.current = null;
      lastBridgeActivityAtRef.current = null;
      hadPriorInboundRef.current = false;
      repairAttemptCountRef.current = 0;
      repairInFlightRef.current = false;
      setIsDrivingCallWindow(true);
      setAttachStatus("opening");
      setErrorMessage(null);

      clearConfirmHandleClosedTimeout();
      clearClosedPoll();
      closedPollRef.current = setInterval(() => {
        if (!windowRef.current?.closed) {
          return;
        }
        const handleClosedAtMs = Date.now();
        windowRef.current = null;
        clearClosedPoll();
        clearConfirmHandleClosedTimeout();
        confirmHandleClosedTimeoutRef.current = setTimeout(() => {
          confirmHandleClosedTimeoutRef.current = null;
          if (
            shouldKeepAutodialerBridgeAfterPopupHandleClosed({
              lastInboundAtMs: lastInboundAtRef.current,
              handleClosedAtMs,
            })
          ) {
            setIsDrivingCallWindow(false);
            return;
          }
          tearingDownRef.current = true;
          clearKeepalivePing();
          clearSilenceWatchdog();
          clearHandshakeTimeout();
          clearRepairBackoff();
          teardownChannel();
          setIsDrivingCallWindow(false);
          setAttachStatus("closed_by_admin");

          // Notify listeners that the popup closed unexpectedly so the call
          // can be treated as ended (without a CALL_ENDED from the popup).
          listenersRef.current.forEach((listener) =>
            listener({ type: "CLOSING" }),
          );
        }, AUTODIALER_STATE_HEARTBEAT_MS + 500);
      }, CLOSED_POLL_INTERVAL_MS);

      clearKeepalivePing();
      keepalivePingRef.current = setInterval(() => {
        if (tearingDownRef.current || windowRef.current?.closed) {
          return;
        }
        postToPopup({ type: "PING" });
      }, AUTODIALER_BRIDGE_KEEPALIVE_PING_MS);

      clearSilenceWatchdog();
      silenceWatchdogRef.current = setInterval(() => {
        if (!canCheckBridgeHealth()) {
          lastBridgeActivityAtRef.current = Date.now();
          return;
        }
        if (
          shouldRecreateAutodialerChannel({
            lastInboundAtMs: lastBridgeActivityAtRef.current,
            nowMs: Date.now(),
            isTearingDown: tearingDownRef.current,
            isWindowClosed: windowRef.current?.closed ?? false,
            isRepairInFlight:
              repairInFlightRef.current || repairBackoffRef.current != null,
            repairAttemptCount: repairAttemptCountRef.current,
            maxRepairAttempts: AUTODIALER_BRIDGE_REPAIR_MAX_ATTEMPTS,
            lastRecreateAtMs: lastRecreateAtRef.current,
          })
        ) {
          startRepairRef.current();
        }
      }, AUTODIALER_BRIDGE_WATCHDOG_INTERVAL_MS);

      bindChannel(popupId);
    },
    [
      bindChannel,
      canCheckBridgeHealth,
      clearClosedPoll,
      clearConfirmHandleClosedTimeout,
      clearHandshakeTimeout,
      clearKeepalivePing,
      clearRepairBackoff,
      clearSilenceWatchdog,
      postToPopup,
      teardownChannel,
    ],
  );

  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const open = useCallback(() => {
    clearCloseTimeout();

    if (windowRef.current && !windowRef.current.closed) {
      attachToWindow(
        popupIdRef.current ??
          (userId != null
            ? getOrCreateAutodialerCallWindowId(userId)
            : _newAutodialerRequestId()),
        windowRef.current,
      );
      return;
    }

    const storedPopupId =
      userId != null ? getStoredAutodialerCallWindowId(userId) : null;
    if (storedPopupId) {
      const existingWindow = window.open(
        "",
        autodialerCallWindowName(storedPopupId),
      );
      if (!existingWindow || existingWindow.closed) {
        setAttachStatus("blocked");
        setErrorMessage(
          "The autodialer call window could not be opened. Allow popups for this site, then try again.",
        );
        return;
      }
      if (isBlankAutodialerCallWindow(existingWindow)) {
        existingWindow.close();
        popupIdRef.current = storedPopupId;
        const shouldSpawn =
          shouldSpawnAutodialerCallWindowAfterBlankProbe(attachStatus);
        if (!channelRef.current) {
          bindChannel(storedPopupId, { listenOnly: !shouldSpawn });
        }
        postToPopup({ type: "CLOSE_DUPLICATE_CALL_WINDOWS" });
        if (!shouldSpawn) {
          return;
        }
        // Open synchronously, still inside this click's user gesture —
        // waiting for a PING response first would let the gesture expire
        // and get this open silently blocked by the browser. If a live
        // popup does answer shortly after, the popup-side sole-lock
        // election (CALL_WINDOW_HELLO) closes whichever one loses, and
        // never one that already has an active call.
        const replacement = window.open(
          autodialerCallWindowPath(storedPopupId),
          autodialerCallWindowName(storedPopupId),
          "popup=yes,width=380,height=560,left=100,top=100",
        );
        if (!replacement || replacement.closed) {
          setAttachStatus("blocked");
          setErrorMessage(
            "The autodialer call window could not be opened. Allow popups for this site, then try again.",
          );
          return;
        }
        attachToWindow(storedPopupId, replacement);
        return;
      }
      attachToWindow(storedPopupId, existingWindow);
      return;
    }

    const popupId =
      userId != null
        ? getOrCreateAutodialerCallWindowId(userId)
        : _newAutodialerRequestId();
    popupIdRef.current = popupId;

    // This id was just created, so passing a URL cannot navigate a live popup.
    const win = window.open(
      autodialerCallWindowPath(popupId),
      autodialerCallWindowName(popupId),
      "popup=yes,width=380,height=560,left=100,top=100",
    );

    if (!win || win.closed) {
      setAttachStatus("blocked");
      setErrorMessage(
        "Your browser blocked the autodialer call window. Allow popups for this site, then try again.",
      );
      return;
    }

    attachToWindow(popupId, win);
  }, [
    attachStatus,
    attachToWindow,
    bindChannel,
    clearCloseTimeout,
    postToPopup,
    userId,
  ]);

  const focus = useCallback(() => {
    const popupId =
      popupIdRef.current ??
      (userId != null ? getStoredAutodialerCallWindowId(userId) : null);
    if (!popupId) {
      open();
      return;
    }

    // Send via BroadcastChannel first — works across browsing-context-group
    // boundaries where window.open("", name) fails in secondary tabs.
    postToPopup({ type: "FOCUS_POPUP" });

    // Same-name `window.open` from a click is what browsers honor for raising
    // a popup. An empty URL reuses the existing window without reloading it.
    // Do not spawn a replacement here: a blank named window usually means
    // this tab cannot see the live popup, not that the popup is gone.
    const win = window.open("", autodialerCallWindowName(popupId));
    if (!win || win.closed) {
      return;
    }

    try {
      if (isBlankAutodialerCallWindow(win)) {
        win.close();
        if (!channelRef.current) {
          bindChannel(popupId, { listenOnly: true });
        }
        postToPopup({ type: "CLOSE_DUPLICATE_CALL_WINDOWS" });
        return;
      }
    } catch {
      // Location can throw if the window is mid-close; still try to focus.
    }

    windowRef.current = win;
    win.focus();
  }, [bindChannel, open, postToPopup, userId]);

  const reattachIfPresent = useCallback(
    (options?: { probeNamedWindow?: boolean }) => {
      if (userId == null) {
        return;
      }
      const popupId = getStoredAutodialerCallWindowId(userId);
      if (!popupId) {
        return;
      }
      popupIdRef.current = popupId;
      tearingDownRef.current = false;

      if (options?.probeNamedWindow === false) {
        bindChannel(popupId, { listenOnly: true });
        return;
      }

      const win = window.open("", autodialerCallWindowName(popupId));
      if (!win || win.closed) {
        bindChannel(popupId, { listenOnly: true });
        return;
      }
      if (isBlankAutodialerCallWindow(win)) {
        win.close();
        bindChannel(popupId, { listenOnly: true });
        return;
      }
      attachToWindow(popupId, win);
    },
    [attachToWindow, bindChannel, userId],
  );

  const close = useCallback(() => {
    tearingDownRef.current = true;
    postToPopup({ type: "CLOSE" });
    clearHandshakeTimeout();
    clearRepairBackoff();
    clearKeepalivePing();
    clearSilenceWatchdog();
    clearClosedPoll();
    clearConfirmHandleClosedTimeout();
    clearCloseTimeout();

    // Capture the current window ref for this close call.
    const windowToClose = windowRef.current;

    // Give the popup a moment to tear down its Device cleanly before we stop
    // listening; force-close if it's still open shortly after.
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      if (windowToClose && !windowToClose.closed) {
        windowToClose.close();
      }
      // Only clear windowRef if it still points at the same window.
      if (windowRef.current === windowToClose) {
        windowRef.current = null;
      }
      teardownChannel();
    }, 1000);
    if (userId != null) {
      clearAutodialerCallWindowId(userId);
    }
    popupIdRef.current = null;
    setIsDrivingCallWindow(false);
    setAttachStatus("idle");
    setErrorMessage(null);
  }, [
    clearClosedPoll,
    clearCloseTimeout,
    clearConfirmHandleClosedTimeout,
    clearHandshakeTimeout,
    clearKeepalivePing,
    clearRepairBackoff,
    clearSilenceWatchdog,
    postToPopup,
    teardownChannel,
    userId,
  ]);

  const dial = useCallback(
    (payload: AutodialerDialPayload) => {
      postToPopup({ type: "CLOSE_DUPLICATE_CALL_WINDOWS" });
      postToPopup({ type: "DIAL", payload });
    },
    [postToPopup],
  );

  const cancelDial = useCallback(
    (taskId: number): Promise<boolean> => {
      const requestId = _newAutodialerRequestId();

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          pendingCancelDialsRef.current.delete(requestId);
          resolve(
            didConfirmAutodialerDialCancel({
              receivedResult: false,
              cancelled: false,
            }),
          );
        }, CANCEL_DIAL_RPC_TIMEOUT_MS);

        pendingCancelDialsRef.current.set(requestId, (cancelled) => {
          clearTimeout(timeout);
          resolve(
            didConfirmAutodialerDialCancel({
              receivedResult: true,
              cancelled,
            }),
          );
        });

        postToPopup({ type: "CANCEL_DIAL", requestId, taskId });
      });
    },
    [postToPopup],
  );

  const hangUp = useCallback(
    (taskId: number, optionId?: string): Promise<void> => {
      const requestId = _newAutodialerRequestId();

      return new Promise((resolve) => {
        const timeout = setTimeout(() => {
          pendingHangUpsRef.current.delete(requestId);
          resolve();
        }, HANG_UP_RPC_TIMEOUT_MS);

        pendingHangUpsRef.current.set(requestId, () => {
          clearTimeout(timeout);
          resolve();
        });

        postToPopup({ type: "HANG_UP", requestId, taskId, optionId });
      });
    },
    [postToPopup],
  );

  const toggleMute = useCallback(() => {
    postToPopup({ type: "TOGGLE_MUTE" });
  }, [postToPopup]);

  const onMessage = useCallback(
    (handler: AutodialerCallWindowMessageHandler) => {
      listenersRef.current.add(handler);
      return () => {
        listenersRef.current.delete(handler);
      };
    },
    [],
  );

  useEffect(() => {
    return () => {
      tearingDownRef.current = true;
      clearHandshakeTimeout();
      clearRepairBackoff();
      clearKeepalivePing();
      clearSilenceWatchdog();
      clearClosedPoll();
      clearConfirmHandleClosedTimeout();
      clearCloseTimeout();
      teardownChannel();
    };
  }, [
    clearClosedPoll,
    clearCloseTimeout,
    clearConfirmHandleClosedTimeout,
    clearHandshakeTimeout,
    clearKeepalivePing,
    clearRepairBackoff,
    clearSilenceWatchdog,
    teardownChannel,
  ]);

  return {
    attachStatus,
    isDrivingCallWindow,
    errorMessage,
    open,
    reattachIfPresent,
    focus,
    close,
    dial,
    cancelDial,
    hangUp,
    toggleMute,
    onMessage,
  };
}

export type { AutodialerCallStatePayload };

function _newAutodialerRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
